/**
 * ReadMoreToggle Component
 * Handles expandable content sections with smooth transitions
 *
 * Contract:
 * - May be bundled globally
 * - Must silently no-op if no read-more buttons exist
 * - Must be idempotent (safe if loaded/constructed multiple times)
 */

class ReadMoreToggle {
  constructor() {
    // Idempotency guard: if this gets constructed multiple times, don't re-bind
    if (window.__readMoreToggleInitialized) return;
    window.__readMoreToggleInitialized = true;

    this.init();
  }

  init() {
    // If the page doesn't have any read-more buttons, silently no-op
    if (!document.querySelector('.read-more-btn')) return;

    this.setupReadMoreButtons();
    this.bindGlobalFunction();
  }

  bindGlobalFunction() {
    // Delegate clicks for robustness and dynamic content support
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!target || !target.classList || !target.classList.contains('read-more-btn')) return;

      event.preventDefault();
      event.stopPropagation();

      // Small delay to ensure DOM is stable
      setTimeout(() => {
        this.toggle(target);
      }, 10);
    });

    // Backward compatibility: only set if not already defined
    if (!window.toggleReadMore) {
      window.toggleReadMore = (button) => {
        setTimeout(() => {
          this.toggle(button);
        }, 10);
      };
    }
  }

  setupReadMoreButtons() {
    const initializeButtons = () => {
      const readMoreButtons = document.querySelectorAll('.read-more-btn');
      if (!readMoreButtons.length) return;

      readMoreButtons.forEach((button, index) => {
        // Skip if already initialized
        if (button.hasAttribute('data-initialized')) return;

        button.setAttribute('data-expanded', 'false');
        button.setAttribute('data-initialized', 'true');

        // Store custom text from data attributes (or use defaults)
        const textMore = button.getAttribute('data-text-more') || 'Read More';
        const textLess = button.getAttribute('data-text-less') || 'Read Less';
        button.setAttribute('data-text-more', textMore);
        button.setAttribute('data-text-less', textLess);

        // Set initial button text and state
        button.textContent = textMore;
        button.classList.remove('expanded');

        const container = this.findContainer(button);
        if (!container) return;

        const expandableContent = container.querySelector('.expandable-content');
        if (!expandableContent) return;

        expandableContent.style.display = 'none';

        const contentId = `expandable-content-${index}-${Date.now()}`;
        expandableContent.setAttribute('id', contentId);

        // Accessibility attributes
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', contentId);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeButtons);
    } else {
      initializeButtons();
    }

    // Catch dynamically loaded content
    setTimeout(initializeButtons, 100);
  }

  findContainer(button) {
    // Primary: ancestor container
    let container = button.closest('.read-more-container');
    if (container) return container;

    // Fallback: search next siblings of ancestors to support older markup
    let parent = button.parentElement;
    while (parent) {
      let nextElement = parent.nextElementSibling;
      while (nextElement) {
        if (nextElement.classList && nextElement.classList.contains('read-more-container')) {
          return nextElement;
        }
        nextElement = nextElement.nextElementSibling;
      }
      parent = parent.parentElement;
    }

    return null;
  }

  toggle(button) {
    if (!button) return;

    // Prevent multiple rapid toggles
    if (button.hasAttribute('data-toggling')) return;
    button.setAttribute('data-toggling', 'true');

    const isExpanded = button.getAttribute('data-expanded') === 'true';

    const container = this.findContainer(button);
    if (!container) {
      button.removeAttribute('data-toggling');
      return;
    }

    const expandableContent = container.querySelector('.expandable-content');
    if (!expandableContent) {
      // Missing content is normal in a shared-bundle world; no console error.
      button.removeAttribute('data-toggling');
      return;
    }

    if (isExpanded) {
      this.collapse(expandableContent, button);
    } else {
      this.expand(expandableContent, button);
    }

    // Remove the toggling flag after animation completes
    setTimeout(() => {
      button.removeAttribute('data-toggling');
    }, 350);
  }

  expand(element, button) {
    // Clear any existing timeouts
    if (element.collapseTimeout) {
      clearTimeout(element.collapseTimeout);
      delete element.collapseTimeout;
    }

    const textLess = button.getAttribute('data-text-less') || 'Read Less';
    button.textContent = textLess;
    button.setAttribute('data-expanded', 'true');
    button.setAttribute('aria-expanded', 'true');
    button.classList.add('expanded');

    element.style.display = 'block';
    element.classList.add('expanding');
    element.classList.remove('collapsing');

    // Ensure the element stays visible
    element.style.opacity = '1';
    element.style.visibility = 'visible';

    setTimeout(() => {
      element.classList.remove('expanding');
    }, 300);

    // Scroll to top of the container so the expanded content is in view
    const container = element.closest('.read-more-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Briefly highlight the container border to draw attention to expanded content
    if (container) {
      if (container._highlightTimeout) {
        clearTimeout(container._highlightTimeout);
      }
      container.classList.remove('border-highlight');
      // Force reflow so re-adding the class restarts the animation
      void container.offsetWidth;
      container.classList.add('border-highlight');
      container._highlightTimeout = setTimeout(() => {
        container.classList.remove('border-highlight');
        delete container._highlightTimeout;
      }, 1000);
    }
  }

  collapse(element, button) {
    // Clear any existing timeouts
    if (element.expandTimeout) {
      clearTimeout(element.expandTimeout);
      delete element.expandTimeout;
    }

    element.classList.add('collapsing');
    element.classList.remove('expanding');

    const textMore = button.getAttribute('data-text-more') || 'Read More';
    button.textContent = textMore;
    button.setAttribute('data-expanded', 'false');
    button.setAttribute('aria-expanded', 'false');
    button.classList.remove('expanded');

    element.collapseTimeout = setTimeout(() => {
      element.style.display = 'none';
      element.classList.remove('collapsing');
      delete element.collapseTimeout;
    }, 300);
  }
}

export default ReadMoreToggle;
